"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { DrawerFieldShell } from "@aera/data-table";
import { AeraButton } from "@aera/ui";
import type { ReceivableListItem } from "@/lib/pendingOrders";
import {
  buildEditAmountInvalidMessage,
  canEditReceivableAmount,
  fetchEditAmountImpact,
  formatEditAmountErrorMessage,
  isEditAmountInvalid,
  pickAmountOriginal,
  pickAmountPaid,
  pickReceivableRevision,
  postEditReceivableAmount,
} from "@/lib/receivableCorrections";
import { createOperationKey } from "@/lib/moneyOperations";
import { offerMoneyUndoToast } from "@/lib/moneyUndoToast";

/** Digits + at most one `.` — blocks `_`, `-`, letters, and other symbols. */
function sanitizeAmountInput(raw: string): string {
  const digitsAndDots = raw.replace(/[^\d.]/g, "");
  const firstDot = digitsAndDots.indexOf(".");
  if (firstDot === -1) return digitsAndDots;
  return (
    digitsAndDots.slice(0, firstDot + 1) +
    digitsAndDots.slice(firstDot + 1).replace(/\./g, "")
  );
}

function parseAmountInput(raw: string): number | null {
  const normalized = sanitizeAmountInput(raw);
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

const INPUT_CLASS =
  "w-full border-0 bg-transparent p-0 text-sm font-semibold leading-6 text-aera-charcoal shadow-none placeholder:text-aera-lightslate placeholder:font-normal focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-aera-lightslate tabular-nums tracking-tight";

function fieldValueForInput(value: string, fallbackAmount = 0): string {
  const trimmed = value.trim();
  if (!trimmed || trimmed === "—") {
    return fallbackAmount > 0 ? String(fallbackAmount) : "";
  }
  const numeric = sanitizeAmountInput(trimmed.replace(/,/g, ""));
  if (numeric) return numeric;
  return fallbackAmount > 0 ? String(fallbackAmount) : "";
}

function AmountEditorPreviewSkeleton() {
  return (
    <div className="space-y-1.5" aria-busy="true" aria-label="กำลังโหลดผลกระทบ">
      <div className="h-3 w-36 animate-pulse rounded bg-aera-lightgray/80" />
      <div className="h-3 w-full max-w-xs animate-pulse rounded bg-aera-lightgray/70" />
      <div className="h-3 w-full max-w-sm animate-pulse rounded bg-aera-lightgray/70" />
    </div>
  );
}

export function ReceivableAnnouncementAmountEditor({
  item,
  label,
  value,
  ownerUserId,
  workspaceId,
  disabled = false,
  onApplied,
}: {
  item: ReceivableListItem;
  label: string;
  value: string;
  ownerUserId: string | null;
  workspaceId: string | null;
  disabled?: boolean;
  onApplied?: () => void | Promise<void>;
}) {
  const receivableId =
    item.receivableId?.trim() ||
    item.fields.find((field) => field.key === "receivable_id")?.value?.trim() ||
    null;
  const originalAmount = useMemo(() => pickAmountOriginal(item), [item]);
  const amountPaid = useMemo(() => pickAmountPaid(item), [item]);
  const editable = canEditReceivableAmount(item);

  const [editAmount, setEditAmount] = useState(() =>
    fieldValueForInput(value, originalAmount),
  );
  const [impactLines, setImpactLines] = useState<string[]>([]);
  const [impactBlocked, setImpactBlocked] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Baseline = last known server state in this drawer (item open / after our save).
  // Impact compares against this to detect remote edits and disable save.
  const [baselineAmount, setBaselineAmount] = useState(originalAmount);
  const [baselineRevision, setBaselineRevision] = useState(() =>
    pickReceivableRevision(item),
  );
  const [expectedRevision, setExpectedRevision] = useState(() =>
    pickReceivableRevision(item),
  );
  const [staleRemote, setStaleRemote] = useState(false);
  const [staleRemoteAmount, setStaleRemoteAmount] = useState<number | null>(
    null,
  );

  const [releaseCandidates, setReleaseCandidates] = useState<
    Array<{ allocation_id: string; payment_id: string; amount: string; sender_name?: string }>
  >([]);
  const [selectedReleasePaymentId, setSelectedReleasePaymentId] = useState("");
  const [requiresRelease, setRequiresRelease] = useState(false);

  useEffect(() => {
    setEditAmount(fieldValueForInput(value, originalAmount));
    setImpactLines([]);
    setImpactBlocked(false);
    setPreviewLoading(false);
    setError(null);
    setReleaseCandidates([]);
    setSelectedReleasePaymentId("");
    setRequiresRelease(false);
    setBaselineAmount(originalAmount);
    const rev = pickReceivableRevision(item);
    setBaselineRevision(rev);
    setExpectedRevision(rev);
    setStaleRemote(false);
    setStaleRemoteAmount(null);
  }, [item.mergeKey, originalAmount, value, item]);

  const canAct = Boolean(
    editable &&
      ownerUserId?.trim() &&
      workspaceId?.trim() &&
      receivableId &&
      !disabled,
  );

  const unchanged =
    parseAmountInput(editAmount) === baselineAmount ||
    (!editAmount.trim() && baselineAmount === 0);

  const invalidAmount = useMemo(
    () => isEditAmountInvalid(editAmount),
    [editAmount],
  );

  const loadPreview = useCallback(async () => {
    if (!canAct || !receivableId || !ownerUserId || !editAmount.trim()) {
      setImpactLines([]);
      setImpactBlocked(false);
      setRequiresRelease(false);
      setReleaseCandidates([]);
      setPreviewLoading(false);
      setError(null);
      return;
    }

    const parsedAmount = parseAmountInput(editAmount);
    if (parsedAmount === null || parsedAmount <= 0) {
      setImpactLines([]);
      setImpactBlocked(false);
      setRequiresRelease(false);
      setPreviewLoading(false);
      setError(null);
      return;
    }

    if (parsedAmount === baselineAmount) {
      setImpactLines([]);
      setImpactBlocked(false);
      setRequiresRelease(false);
      setPreviewLoading(false);
      setStaleRemote(false);
      setStaleRemoteAmount(null);
      setError(null);
      return;
    }

    setPreviewLoading(true);
    setError(null);
    try {
      const impact = await fetchEditAmountImpact(
        receivableId,
        ownerUserId,
        String(parsedAmount),
      );
      const blocked = impact.blocked === true;
      setImpactBlocked(blocked);
      setRequiresRelease(impact.requires_release === true);
      setImpactLines(impact.summary_lines ?? []);

      const freshRevision = Number(impact.current?.revision);
      const serverAmount = parseAmountInput(
        String(impact.current?.amount ?? ""),
      );
      // Disable save only when the announced amount itself moved (another edit).
      // Revision-only bumps still refresh expectedRevision silently for OCC.
      const amountDrifted =
        serverAmount !== null &&
        Math.abs(serverAmount - baselineAmount) > 0.005;

      if (Number.isFinite(freshRevision) && freshRevision > 0) {
        setExpectedRevision(freshRevision);
      }

      if (amountDrifted) {
        setStaleRemote(true);
        setStaleRemoteAmount(serverAmount);
      } else {
        setStaleRemote(false);
        setStaleRemoteAmount(null);
      }

      const candidates = impact.release_candidates ?? [];
      setReleaseCandidates(candidates);
      if (candidates.length === 1) {
        setSelectedReleasePaymentId(candidates[0].payment_id);
      } else if (candidates.length > 1 && !selectedReleasePaymentId) {
        setSelectedReleasePaymentId("");
      }
    } catch (err) {
      setImpactLines([]);
      setImpactBlocked(false);
      setRequiresRelease(false);
      setStaleRemote(false);
      setStaleRemoteAmount(null);
      setError(
        formatEditAmountErrorMessage(
          err instanceof Error ? err.message : "โหลดผลกระทบไม่สำเร็จ",
        ),
      );
    } finally {
      setPreviewLoading(false);
    }
  }, [
    baselineAmount,
    canAct,
    editAmount,
    ownerUserId,
    receivableId,
    selectedReleasePaymentId,
  ]);

  useEffect(() => {
    if (!canAct || unchanged) {
      setPreviewLoading(false);
      return;
    }
    const parsedAmount = parseAmountInput(editAmount);
    if (parsedAmount === null || parsedAmount <= 0) {
      setPreviewLoading(false);
      return;
    }
    setPreviewLoading(true);
  }, [canAct, editAmount, unchanged]);

  useEffect(() => {
    if (!canAct) return;
    const timer = window.setTimeout(() => {
      void loadPreview();
    }, 350);
    return () => window.clearTimeout(timer);
  }, [canAct, loadPreview, editAmount]);

  if (!editable) {
    return null;
  }

  const handleSave = async () => {
    if (!canAct || !receivableId || !ownerUserId || !workspaceId) return;
    if (
      unchanged ||
      impactBlocked ||
      invalidAmount ||
      previewLoading ||
      busy ||
      staleRemote
    ) {
      return;
    }

    const parsedAmount = parseAmountInput(editAmount);
    if (parsedAmount === null || parsedAmount <= 0) {
      setError(buildEditAmountInvalidMessage());
      return;
    }

    setBusy(true);
    setError(null);

    let releasePlan:
      | Array<{ allocation_id?: string; payment_id?: string; amount: string }>
      | undefined;
    if (requiresRelease && parsedAmount < amountPaid) {
      const excess = amountPaid - parsedAmount;
      if (releaseCandidates.length > 1) {
        if (!selectedReleasePaymentId) {
          setBusy(false);
          setError("กรุณาเลือกสลิปที่จะคืนส่วนเกิน");
          return;
        }
        const candidate = releaseCandidates.find(
          (c) => c.payment_id === selectedReleasePaymentId,
        );
        if (!candidate) {
          setBusy(false);
          setError("ไม่พบสลิปที่เลือก");
          return;
        }
        releasePlan = [
          {
            allocation_id: candidate.allocation_id,
            payment_id: candidate.payment_id,
            amount: String(excess),
          },
        ];
      }
      // single payment: backend LIFO — omit plan
    }

    try {
      await postEditReceivableAmount({
        receivableId,
        ownerUserId,
        workspaceId,
        newAmount: String(parsedAmount),
        expectedRevision,
        releasePlan,
        operationKey: createOperationKey("edit_amt"),
        receivableItem: item,
      }).then(async (result) => {
        const afterRevision = Number(
          (result.after as { revision?: unknown } | undefined)?.revision,
        );
        if (Number.isFinite(afterRevision) && afterRevision > 0) {
          setExpectedRevision(afterRevision);
          setBaselineRevision(afterRevision);
        }
        setBaselineAmount(parsedAmount);
        setStaleRemote(false);
        setStaleRemoteAmount(null);
        const opId =
          typeof result.after?.operation_id === "string"
            ? result.after.operation_id
            : null;
        offerMoneyUndoToast({
          message: (() => {
            const name =
              item.title?.trim() ||
              item.fields.find((f) => f.key === "customer_ref")?.value?.trim() ||
              item.fields.find((f) => f.key === "customer_name")?.value?.trim() ||
              "ยอดค้าง";
            const fromLabel = baselineAmount.toLocaleString("th-TH", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            });
            const toLabel = parsedAmount.toLocaleString("th-TH", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            });
            return `แก้ยอดแจ้ง · ${name} ${fromLabel} → ${toLabel} บาท`;
          })(),
          ownerUserId,
          workspaceId,
          operationId: opId,
          onUndone: onApplied,
        });
      });
      await onApplied?.();
    } catch (err) {
      if (
        err instanceof Error &&
        /revision conflict/i.test(err.message) &&
        receivableId &&
        ownerUserId
      ) {
        try {
          const impact = await fetchEditAmountImpact(
            receivableId,
            ownerUserId,
            String(parsedAmount),
          );
          const freshRevision = Number(impact.current?.revision);
          const serverAmount = parseAmountInput(
            String(impact.current?.amount ?? ""),
          );
          if (Number.isFinite(freshRevision) && freshRevision > 0) {
            setExpectedRevision(freshRevision);
          }
          setStaleRemote(true);
          setStaleRemoteAmount(serverAmount);
        } catch {
          setStaleRemote(true);
        }
      }
      setError(
        formatEditAmountErrorMessage(
          err instanceof Error ? err.message : "แก้ยอดแจ้งไม่สำเร็จ",
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleAdoptRemote = async () => {
    const amount = staleRemoteAmount;
    if (amount !== null) {
      setEditAmount(String(amount));
      setBaselineAmount(amount);
    }
    if (expectedRevision > 0) {
      setBaselineRevision(expectedRevision);
    }
    setStaleRemote(false);
    setStaleRemoteAmount(null);
    setImpactLines([]);
    setError(null);
    await onApplied?.();
  };

  return (
    <div className="space-y-2 px-4 py-3">
      <label
        htmlFor="receivable-field-amount_original"
        className="block text-xs font-semibold leading-5 text-aera-lightslate"
      >
        {label}
      </label>
      <DrawerFieldShell editable={!disabled && canAct}>
        <input
          id="receivable-field-amount_original"
          type="text"
          inputMode="decimal"
          value={editAmount}
          disabled={!canAct || busy}
          placeholder="ยังไม่ระบุ"
          onChange={(event) =>
            setEditAmount(sanitizeAmountInput(event.target.value))
          }
          onKeyDown={(event) => {
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            const { key } = event;
            if (key.length === 1 && key !== "." && !/^\d$/.test(key)) {
              event.preventDefault();
            }
          }}
          className={INPUT_CLASS}
        />
      </DrawerFieldShell>

      {amountPaid > 0 && unchanged ? (
        <p className="text-xs text-aera-lightslate">
          จับคู่รายรับแล้ว {amountPaid.toLocaleString("th-TH")} บาท
        </p>
      ) : null}

      {!unchanged && previewLoading ? <AmountEditorPreviewSkeleton /> : null}

      {!unchanged && !previewLoading && invalidAmount ? (
        <p className="text-xs font-medium text-aera-red">
          {buildEditAmountInvalidMessage()}
        </p>
      ) : null}

      {!unchanged && !previewLoading && !invalidAmount && staleRemote ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-aera-red">
            รายการนี้ถูกแก้ไปแล้ว
            {staleRemoteAmount !== null
              ? ` (ยอดปัจจุบัน ${staleRemoteAmount.toLocaleString("th-TH")} บาท)`
              : ""}
          </p>
          <AeraButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => void handleAdoptRemote()}
          >
            ใช้ยอดล่าสุด
          </AeraButton>
        </div>
      ) : null}

      {!unchanged &&
      !previewLoading &&
      !invalidAmount &&
      !staleRemote &&
      impactLines.length > 0 ? (
        <ul
          className={`space-y-1 text-xs ${
            impactBlocked ? "font-medium text-aera-red" : "text-aera-gray"
          }`}
        >
          {impactLines.map((line) => (
            <li key={line}>• {line}</li>
          ))}
        </ul>
      ) : null}

      {requiresRelease &&
      releaseCandidates.length > 1 &&
      !impactBlocked &&
      !staleRemote ? (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-aera-charcoal">
            เลือกสลิปที่จะคืนส่วนเกิน
          </p>
          {releaseCandidates.map((c) => (
            <label
              key={c.allocation_id}
              className="flex cursor-pointer items-center gap-2 text-xs text-aera-gray"
            >
              <input
                type="radio"
                name="release-payment"
                checked={selectedReleasePaymentId === c.payment_id}
                onChange={() => setSelectedReleasePaymentId(c.payment_id)}
              />
              <span>
                {(c.sender_name || "สลิป").trim()} · {c.amount} บาท
              </span>
            </label>
          ))}
        </div>
      ) : null}

      {!unchanged && canAct && !invalidAmount && !staleRemote ? (
        <AeraButton
          type="button"
          variant="secondary"
          size="sm"
          disabled={
            busy ||
            previewLoading ||
            impactBlocked ||
            !editAmount.trim() ||
            (requiresRelease &&
              releaseCandidates.length > 1 &&
              !selectedReleasePaymentId)
          }
          onClick={() => void handleSave()}
        >
          {busy
            ? "กำลังบันทึก…"
            : previewLoading
              ? "กำลังโหลด…"
              : "บันทึกยอดแจ้ง"}
        </AeraButton>
      ) : null}

      {error && !impactBlocked && !staleRemote ? (
        <p className="text-xs font-medium text-aera-red">{error}</p>
      ) : null}
    </div>
  );
}
